/**
 * Defensive access to Web Storage (#622, Sentry CLUB-J).
 *
 * In privacy modes, with site data blocked, or inside some in-app browsers the
 * browser does not merely return null — it throws:
 *
 *   SecurityError: Failed to read the 'localStorage' property from 'Window':
 *   Access is denied for this document.
 *
 * Note WHERE it throws: on the `localStorage` PROPERTY ACCESS itself, not on
 * `getItem`. That is why every function below re-enters `window.localStorage`
 * inside its own try/catch and nothing ever caches the storage object.
 *
 * Unavailability is STICKY: the first failure in this document marks storage as
 * gone for good and every later call short-circuits without touching it again.
 * Two reasons:
 *   (a) the HTTP interceptor reads the token on every single request — we do
 *       not want an exception thrown (and swallowed) per request;
 *   (b) correctness on logout: if `clearToken()` could not reach storage, a
 *       later read must NOT be allowed to resurrect the old persisted token
 *       should storage start answering again.
 */

let storageUnavailable = false;

/** False once any storage access has failed in this document (sticky). */
export function isStorageAvailable(): boolean {
  return !storageUnavailable;
}

export function storageGet(key: string): string | null {
  if (storageUnavailable) {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch {
    storageUnavailable = true;
    return null;
  }
}

export function storageSet(key: string, value: string): void {
  if (storageUnavailable) {
    return;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Also covers QuotaExceededError, not just the blocked-storage SecurityError.
    storageUnavailable = true;
  }
}

export function storageRemove(key: string): void {
  if (storageUnavailable) {
    return;
  }
  try {
    window.localStorage.removeItem(key);
  } catch {
    storageUnavailable = true;
  }
}

/**
 * TEST-ONLY. Clears the sticky latch, which is module-level state and would
 * otherwise leak from one test to the next. Never call this from app code.
 */
export function __resetStorageGuardForTests(): void {
  storageUnavailable = false;
}
